<template>
  <div class="magdeburg-viz-pro">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex><strong>公式：</strong>P ≈ 101 kPa</div>
    </div>

    <div class="main-layout vertical">
      <!-- 3D 模拟区 -->
      <div ref="canvasContainer" class="canvas-3d-wrapper">
         <div class="hud-top">
            <div class="gas-meter">
               <label>内部气压</label>
               <span :class="{ 'vacuum': isVacuum }">{{ isVacuum ? '0.01' : '1.01' }} bar</span>
            </div>
         </div>
         
         <div class="force-hud" v-if="isVacuum && isAttempting">
            <div class="force-arrow L"></div>
            <div class="text">10³ N</div>
            <div class="force-arrow R"></div>
         </div>
      </div>

      <!-- 控制面板 -->
      <div class="bottom-controls">
         <div class="action-card glass">
            <div class="steps">
               <div class="step" :class="{ done: isVacuum }">
                  <el-button type="primary" size="mini" @click="pumpOut" :disabled="isVacuum">1. 抽成真空</el-button>
               </div>
               <div class="step" :class="{ active: isVacuum }">
                  <el-button type="danger" size="mini" @mousedown.native="startPull" @mouseup.native="stopPull" :disabled="!isVacuum">2. 用力拉开</el-button>
               </div>
               <div class="step">
                  <el-button size="mini" @click="reset">3. 放气/重置</el-button>
               </div>
            </div>
         </div>

         <div class="theory-card">
            <i class="el-icon-info"></i>
            <p><strong>原理说明：</strong>当半球内被抽成真空后，内部失去向外的支撑压力，外界巨大的<strong>大气压强</strong>将两个半球紧紧压在一起。即使是强壮的马队也难以将其拉开。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'MagdeburgViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      isVacuum: false,
      isAttempting: false,
      pullOffset: 0,
      running: true
    }
  },
  mounted() {
    this.initThree();
  },
  beforeDestroy() {
    this.running = false;
    if (this.renderer) {
      this.renderer.dispose();
      this.canvasContainer.removeChild(this.renderer.domElement);
    }
  },
  methods: {
    initThree() {
      this.canvasContainer = this.$refs.canvasContainer;
      const width = this.canvasContainer.clientWidth;
      const height = this.canvasContainer.clientHeight;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
      this.camera.position.set(0, 30, 80);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.canvasContainer.appendChild(this.renderer.domElement);

      // Hemispheres
      const hemiGeo = new THREE.SphereGeometry(20, 32, 32, 0, Math.PI * 2, 0, Math.PI / 2);
      const hemiMat = new THREE.MeshPhongMaterial({ color: 0x666, side: THREE.DoubleSide, shininess: 100 });
      
      this.hemiL = new THREE.Mesh(hemiGeo, hemiMat);
      this.hemiL.rotation.z = Math.PI / 2;
      this.scene.add(this.hemiL);

      this.hemiR = new THREE.Mesh(hemiGeo, hemiMat);
      this.hemiR.rotation.z = -Math.PI / 2;
      this.scene.add(this.hemiR);

      // Force Arrows (External Pressure)
      this.arrows = new THREE.Group();
      for(let i=0; i<12; i++) {
          const angle = (i / 12) * Math.PI * 2;
          const arrow = new THREE.ArrowHelper(new THREE.Vector3(-Math.cos(angle), -Math.sin(angle), 0).normalize(), new THREE.Vector3(30*Math.cos(angle), 30*Math.sin(angle), 0), 8, 0x00d2ff, 4, 1.5);
          this.arrows.add(arrow);
      }
      this.scene.add(this.arrows);

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.4));
      const light = new THREE.DirectionalLight(0xffffff, 0.8);
      light.position.set(0, 50, 50);
      this.scene.add(light);

      this.animate();
    },
    pumpOut() { this.isVacuum = true; },
    startPull() { this.isAttempting = true; },
    stopPull() { this.isAttempting = false; },
    reset() { 
      this.isVacuum = false; 
      this.isAttempting = false; 
      this.pullOffset = 0;
    },
    animate() {
      if (!this.running) return;
      requestAnimationFrame(this.animate);
      
      const targetOffset = this.isAttempting ? 2 : 0;
      this.pullOffset += (targetOffset - this.pullOffset) * 0.1;

      this.hemiL.position.x = -this.pullOffset;
      this.hemiR.position.x = this.pullOffset;
      
      this.arrows.visible = this.isVacuum;

      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.magdeburg-viz-pro {
  flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden;
}
.viz-header {
  display: flex; justify-content: space-between; align-items: center;
  .title { font-size: 1.1rem; color: #fff; font-weight: 300; }
  .formula-chip { color: #ff4757; background: rgba(255, 71, 87, 0.1); padding: 4px 12px; border-radius: 8px; font-weight: bold; }
}
.canvas-3d-wrapper {
  flex: 1.3; background: #000; border-radius: 12px; position: relative; overflow: hidden;
  .hud-top {
     position: absolute; top: 15px; left: 15px;
     .gas-meter {
        background: rgba(10, 10, 20, 0.7); backdrop-filter: blur(5px); border-left: 3px solid #ff4757; padding: 5px 12px; border-radius: 6px;
        label { display: block; font-size: 0.55rem; color: #888; }
        span { font-size: 0.9rem; color: #fff; font-family: monospace; &.vacuum { color: #2ed573; } }
     }
  }
  .force-hud {
     position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); display: flex; align-items: center; gap: 20px;
     .text { font-size: 1.5rem; font-weight: 900; color: #ff4757; text-shadow: 0 0 10px #ff4757; }
  }
}
.bottom-controls { flex: 0.7; display: flex; flex-direction: column; gap: 12px; overflow-y: auto; padding-bottom: 10px; }
.action-card {
   background: rgba(255, 255, 255, 0.03); border-radius: 12px; padding: 15px;
   .steps { display: flex; justify-content: space-between; align-items: center; }
}
.theory-card {
   background: rgba(255, 71, 87, 0.05); border-radius: 12px; padding: 12px 18px; display: flex; gap: 12px; align-items: flex-start;
   i { color: #ff4757; font-size: 1.1rem; margin-top: 2px; }
   p { margin: 0; font-size: 0.75rem; color: #888; line-height: 1.5; strong { color: #fff; } }
}
</style>
